import React, { useEffect, useState } from 'react';
import { Card, Btn, Input, Field, Select, Spinner, ErrBox } from '../../../components/UI';
import { procurementAPI, inventoryAPI, masterAPI } from '../../../utils/api';
import { assetsAPI, budgetAPI } from '../../../utils/api';
import { fmtRp } from '../../../utils/format';

const levelBadge = (val, color) => val ? (
  <span style={{ display:"inline-block", padding:"2px 8px", borderRadius:12, fontSize:11, fontWeight:600, background:`${color}22`, color, border:`1px solid ${color}44` }}>{val}</span>
) : null;
const ProgramBadge = ({v}) => levelBadge(v, "#7C3AED");
const KegiatanBadge = ({v}) => levelBadge(v, "#D97706");
const KroBadge = ({v}) => levelBadge(v, "#059669");

export default function CreateTab({ onSuccess }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [units, setUnits] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [assetCategories, setAssetCategories] = useState([]);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [locations, setLocations] = useState([]);
  const [anggaran, setAnggaran] = useState([]);
  const [allAkun, setAllAkun] = useState([]);
  const [filteredAkun, setFilteredAkun] = useState([]);
  const [refCoaDetail, setRefCoaDetail] = useState([]);
  const [form, setForm] = useState({ request_type: 'ASSET', request_date: new Date().toISOString().split('T')[0], requester_unit_id: '', vendor_id: '', anggaran_id: '', akun_id: '', coa_detail_id: '', notes: '', items: [] });
  const [cur, setCur] = useState({ asset_category_id: '', asset_name: '', item_id: '', location_id: '', description: '', quantity: 1, unit_price: 0 });

  useEffect(() => { loadRefs(); }, []);

  const loadRefs = async () => {
    try {
      setLoading(true);
      const [u, v, ac, ii, loc, a, c, cd] = await Promise.all([
        masterAPI.getUnit(),
        masterAPI.getVendor(),
        assetsAPI.getCategories().catch(() => ({ data: [] })),
        inventoryAPI.getItems('?limit=500'),
        inventoryAPI.getLocations(),
        budgetAPI.getAll('?limit=9999&status=AKTIF').catch(() => ({ data: [] })),
        masterAPI.getCoaAkun(),
        masterAPI.getCoaDetailAkun(),
      ]);
      setUnits((u.data || []).map(x => ({ value: x.id, label: x.nama })));
      setVendors((v.data || []).map(x => ({ value: x.id, label: x.nama })));
      setAssetCategories((ac.data || []).map(x => ({ value: x.id, label: x.name })));
      setInventoryItems((ii.data || []).map(x => ({ value: x.id, label: `${x.kode} - ${x.nama}` })));
      setLocations((loc.data || []).map(x => ({ value: x.id, label: x.nama })));

      const angData = (a.data || []).map(x => ({
        value: x.id,
        label: `[${x.tahun_anggaran}] ${x.kode_akun} - ${x.nama_akun || x.kegiatan_nama || x.keterangan || '-'} | Sisa: ${Number(x.sisa_pagu||0).toLocaleString('id-ID')}`,
        pagu: x.pagu,
        realized: x.realized,
        sisa: x.sisa_pagu,
        program_kode: x.program_kode, program_nama: x.program_nama,
        kegiatan_kode: x.kegiatan_kode, kegiatan_nama: x.kegiatan_nama,
        kro_kode: x.kro_kode, kro_nama: x.kro_nama,
        program_id: x.mp_id,
        kegiatan_id: x.mk_id,
        kro_id: x.kr_id,
      }));
      setAnggaran(angData);

      const akunData = (c.data || []).map(x => ({
        value: x.id,
        label: `${x.kode} - ${x.nama}`,
        program_kode: x.program_kode, program_nama: x.program_nama,
        kegiatan_kode: x.kegiatan_kode, kegiatan_nama: x.kegiatan_nama,
        kro_kode: x.kro_kode, kro_nama: x.kro_nama,
        program_id: x.program_id,
        kegiatan_id: x.kegiatan_id,
        kro_id: x.kro_id,
      }));
      setAllAkun(akunData);
      setFilteredAkun(akunData);
      setRefCoaDetail((cd.data||[]).map(x=>({value:x.id, label:`${x.kode} - ${x.nama}`, akun_id:x.akun_id})));
      setError(null);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  const handleAnggaranChange = (angId) => {
    const sel = anggaran.find(a => a.value === angId);
    if (!angId || !sel) {
      setForm(f => ({ ...f, anggaran_id: angId, akun_id: '' }));
      setFilteredAkun(allAkun);
      return;
    }
    let filtered = allAkun;
    if (sel.kro_id) {
      filtered = allAkun.filter(a => a.kro_id === sel.kro_id);
    } else if (sel.kegiatan_id) {
      filtered = allAkun.filter(a => a.kegiatan_id === sel.kegiatan_id);
    } else if (sel.program_id) {
      filtered = allAkun.filter(a => a.program_id === sel.program_id);
    }
    if (filtered.length === 0) filtered = allAkun;
    setFilteredAkun(filtered);
    setForm(f => ({ ...f, anggaran_id: angId, akun_id: '', coa_detail_id: '' }));
  };

  const handleAkunChange = (akunId) => {
    setForm(f => ({ ...f, akun_id: akunId, coa_detail_id: '' }));
  };

  const filteredCoaDetail = form.akun_id ? refCoaDetail.filter(d => String(d.akun_id) === String(form.akun_id)) : [];
  const selectedCoaDetail = refCoaDetail.find(d => d.value === form.coa_detail_id);

  const totalAmount = () => form.items.reduce((sum, i) => sum + (i.quantity || 0) * (i.unit_price || 0), 0);

  const addItem = () => {
    if (form.request_type === 'ASSET') {
      if (!cur.asset_category_id || !cur.asset_name) return alert('Kategori dan Nama Asset wajib diisi');
    } else {
      if (!cur.item_id) return alert('Barang inventory wajib dipilih');
    }
    if (!cur.quantity || cur.quantity <= 0) return alert('Qty wajib lebih dari 0');
    if (!cur.unit_price || cur.unit_price <= 0) return alert('Harga wajib lebih dari 0');
    setForm({ ...form, items: [...form.items, { ...cur, line_total: cur.quantity * cur.unit_price }] });
    setCur({ asset_category_id: '', asset_name: '', item_id: '', location_id: '', description: '', quantity: 1, unit_price: 0 });
  };

  const removeItem = (idx) => setForm({ ...form, items: form.items.filter((_, i) => i !== idx) });

  const handleSave = async () => {
    if (!form.requester_unit_id) return alert('Unit wajib diisi');
    if (form.items.length === 0) return alert('Minimal 1 item wajib ditambahkan');
    if (totalAmount() <= 0) return alert('Total nilai harus lebih dari 0');
    setSaving(true);
    try {
      const payload = {
        request_type: form.request_type,
        request_date: form.request_date,
        requester_unit_id: form.requester_unit_id,
        vendor_id: form.vendor_id || null,
        anggaran_id: form.anggaran_id || null,
        akun_id: form.akun_id || null,
        coa_detail_id: form.coa_detail_id || null,
        notes: form.notes || null,
        items: form.items.map(i => ({
          item_type: form.request_type,
          asset_category_id: form.request_type === 'ASSET' ? i.asset_category_id : null,
          asset_name: form.request_type === 'ASSET' ? i.asset_name : null,
          item_id: form.request_type === 'INVENTORY' ? i.item_id : null,
          location_id: i.location_id || null,
          description: i.description || null,
          quantity: i.quantity,
          unit_price: i.unit_price,
        })),
      };
      await procurementAPI.create(payload);
      onSuccess();
    } catch (e) { alert('Gagal: ' + e.message); }
    finally { setSaving(false); }
  };

  const getItemLabel = (item) => {
    if (form.request_type === 'ASSET') {
      const cat = assetCategories.find(c => c.value === item.asset_category_id);
      return `${cat?.label || ''} - ${item.asset_name}`;
    }
    const inv = inventoryItems.find(i => i.value === item.item_id);
    return inv?.label || item.item_id;
  };

  const selectedAkun = filteredAkun.find(x => x.value === form.akun_id);
  const selectedAng = anggaran.find(a => a.value === form.anggaran_id);

  if (loading) return <div style={{ padding: '2rem', textAlign: 'center' }}><Spinner /></div>;
  if (error) return <ErrBox msg={error} onRetry={loadRefs} />;

  return (
    <div>
      <Card>
        <h3 style={{ margin: '0 0 20px', fontSize: 16, fontWeight: 700, color: '#111827' }}>Permintaan Procurement Baru</h3>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
          <Field label="Tipe">
            <select value={form.request_type} onChange={e => setForm({ ...form, request_type: e.target.value, items: [] })}
              style={{ width: '100%', padding: '9px 12px', border: '1.5px solid #E5E7EB', borderRadius: 8, fontSize: 13 }}>
              <option value="ASSET">Asset (Barang Berwujud)</option>
              <option value="INVENTORY">Inventory (Barang Persediaan)</option>
            </select>
          </Field>
          <Field label="Tanggal">
            <Input type="date" value={form.request_date} onChange={e => setForm({ ...form, request_date: e.target.value })} />
          </Field>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
          <Field label="Unit Pemohon" required>
            <Select value={form.requester_unit_id} onChange={e => setForm({ ...form, requester_unit_id: e.target.value })} options={units} placeholder="Pilih unit..." />
          </Field>
          <Field label="Vendor">
            <Select value={form.vendor_id} onChange={e => setForm({ ...form, vendor_id: e.target.value })} options={[{ value: '', label: 'Belum ditentukan' }, ...vendors]} />
          </Field>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
          <Field label="Anggaran (DIPA)" required>
            <Select value={form.anggaran_id} onChange={e => handleAnggaranChange(e.target.value)} options={[{ value: '', label: 'Tanpa anggaran' }, ...anggaran]} />
          </Field>
          <Field label="Akun MA">
            <Select value={form.akun_id} onChange={e => handleAkunChange(e.target.value)} options={[{ value: '', label: 'Pilih akun...' }, ...filteredAkun]} />
          </Field>
          {form.akun_id && (
            <Field label="Detail COA" required>
              <Select value={form.coa_detail_id} onChange={e => setForm(f => ({...f, coa_detail_id: e.target.value}))} options={filteredCoaDetail} placeholder="Pilih Detail COA..." />
              {selectedCoaDetail && (
                <div style={{ marginTop:8, padding:8, background:"#EFF6FF", borderRadius:8, fontSize:12 }}>
                  <span style={{fontFamily:"monospace",fontWeight:700,color:"#2563EB"}}>{selectedCoaDetail.label}</span>
                </div>
              )}
            </Field>
          )}
        </div>

        {selectedAng && (
          <div style={{ marginBottom: 16, padding:10, background:"#F0FDF4", borderRadius:8, fontSize:12, display:"flex", flexDirection:"column", gap:6 }}>
            <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
              <ProgramBadge v={selectedAng.program_kode ? `${selectedAng.program_kode} - ${selectedAng.program_nama}` : null}/>
              <KegiatanBadge v={selectedAng.kegiatan_kode ? `${selectedAng.kegiatan_kode} - ${selectedAng.kegiatan_nama}` : null}/>
              <KroBadge v={selectedAng.kro_kode ? `${selectedAng.kro_kode} - ${selectedAng.kro_nama}` : null}/>
            </div>
            {selectedAng.pagu && (
              <div style={{ color:"#047857", fontFamily:"monospace", fontWeight:600 }}>
                Pagu: {fmtRp(selectedAng.pagu)} | Sisa: {fmtRp(selectedAng.sisa || 0)}
              </div>
            )}
          </div>
        )}

        {form.anggaran_id && (
          <div style={{ marginBottom: 16, padding:8, background:"#EEF2FF", borderRadius:6, fontSize:11, color:"#4338CA" }}>
            Akun MA difilter berdasarkan DIPA yang dipilih ({filteredAkun.length} akun tersedia)
          </div>
        )}

        {selectedAkun && (
          <div style={{ marginBottom: 16, padding:10, background:"#F8FAFC", borderRadius:8, fontSize:12, display:"flex", gap:6, flexWrap:"wrap" }}>
            <ProgramBadge v={selectedAkun.program_kode ? `${selectedAkun.program_kode} - ${selectedAkun.program_nama}` : null}/>
            <KegiatanBadge v={selectedAkun.kegiatan_kode ? `${selectedAkun.kegiatan_kode} - ${selectedAkun.kegiatan_nama}` : null}/>
            <KroBadge v={selectedAkun.kro_kode ? `${selectedAkun.kro_kode} - ${selectedAkun.kro_nama}` : null}/>
          </div>
        )}

        <Field label="Catatan / Keterangan">
          <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={2}
            style={{ width: '100%', padding: '9px 12px', border: '1.5px solid #E5E7EB', borderRadius: 8, fontSize: 13, resize: 'vertical', boxSizing: 'border-box' }}
            placeholder="Keterangan tambahan..." />
        </Field>
      </Card>

      <Card style={{ marginTop: 16 }}>
        <h4 style={{ margin: '0 0 12px', fontSize: 13, fontWeight: 600, color: '#374151' }}>
          Item Procurement — {form.request_type === 'ASSET' ? 'Aset Tetap' : 'Barang Persediaan'}
        </h4>

        {form.request_type === 'ASSET' ? (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr 1fr 1fr 1fr auto', gap: 10, alignItems: 'flex-end', background: '#F8FAFC', borderRadius: 10, padding: 16, marginBottom: 12 }}>
            <Field label="Kategori Asset">
              <Select value={cur.asset_category_id} onChange={e => setCur({ ...cur, asset_category_id: e.target.value })} options={assetCategories} placeholder="Pilih kategori..." />
            </Field>
            <Field label="Nama Asset">
              <Input value={cur.asset_name} onChange={e => setCur({ ...cur, asset_name: e.target.value })} placeholder="Nama aset yang diminta" />
            </Field>
            <Field label="Qty">
              <Input type="number" value={cur.quantity || ''} min={1} onChange={e => setCur({ ...cur, quantity: parseInt(e.target.value) || 1 })} />
            </Field>
            <Field label="Harga/Unit">
              <Input type="number" value={cur.unit_price || ''} min={0} onChange={e => setCur({ ...cur, unit_price: parseInt(e.target.value) || 0 })} />
            </Field>
            <Field label="Deskripsi">
              <Input value={cur.description} onChange={e => setCur({ ...cur, description: e.target.value })} placeholder="Spesifikasi" />
            </Field>
            <Btn size="sm" onClick={addItem}>+</Btn>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr auto', gap: 10, alignItems: 'flex-end', background: '#F8FAFC', borderRadius: 10, padding: 16, marginBottom: 12 }}>
            <Field label="Barang (Item Master)">
              <Select value={cur.item_id} onChange={e => setCur({ ...cur, item_id: e.target.value })} options={inventoryItems} placeholder="Pilih dari item master..." />
            </Field>
            <Field label="Gudang">
              <Select value={cur.location_id} onChange={e => setCur({ ...cur, location_id: e.target.value })} options={[{ value: '', label: '-' }, ...locations]} />
            </Field>
            <Field label="Qty">
              <Input type="number" value={cur.quantity || ''} min={1} onChange={e => setCur({ ...cur, quantity: parseInt(e.target.value) || 1 })} />
            </Field>
            <Field label="Harga/Unit">
              <Input type="number" value={cur.unit_price || ''} min={0} onChange={e => setCur({ ...cur, unit_price: parseInt(e.target.value) || 0 })} />
            </Field>
            <Btn size="sm" onClick={addItem}>+</Btn>
          </div>
        )}

        {form.items.length > 0 && (
          <>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead><tr style={{ background: '#F8FAFC' }}>
                <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, color: '#6B7280', fontSize: 11 }}>Nama / Barang</th>
                <th style={{ padding: '8px 12px', textAlign: 'center', fontWeight: 600, color: '#6B7280', fontSize: 11 }}>Qty</th>
                <th style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600, color: '#6B7280', fontSize: 11 }}>Harga</th>
                <th style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600, color: '#6B7280', fontSize: 11 }}>Subtotal</th>
                <th style={{ padding: '8px 12px', width: 50 }}></th>
              </tr></thead>
              <tbody>
                {form.items.map((item, idx) => (
                  <tr key={idx} style={{ borderBottom: '1px solid #F3F4F6' }}>
                    <td style={{ padding: '8px 12px' }}>{getItemLabel(item)}</td>
                    <td style={{ padding: '8px 12px', textAlign: 'center' }}>{item.quantity}</td>
                    <td style={{ padding: '8px 12px', textAlign: 'right', fontFamily: 'monospace' }}>{fmtRp(item.unit_price)}</td>
                    <td style={{ padding: '8px 12px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 600 }}>{fmtRp(item.line_total)}</td>
                    <td style={{ padding: '8px 12px' }}><Btn size="sm" variant="danger" onClick={() => removeItem(idx)}>X</Btn></td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '12px 0' }}>
              <span style={{ fontSize: 14, fontWeight: 700 }}>Total: </span>
              <span style={{ fontSize: 16, fontWeight: 700, color: '#2563EB', fontFamily: 'monospace' }}>{fmtRp(totalAmount())}</span>
            </div>
          </>
        )}
      </Card>

      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
        <Btn variant="secondary" onClick={onSuccess}>Batal</Btn>
        <Btn onClick={handleSave} disabled={saving}>{saving ? 'Menyimpan...' : 'Simpan PR'}</Btn>
      </div>
    </div>
  );
}